/**
 * 文件下载工具类
 * 提供多种文件下载方式
 */

/**
 * MIME类型映射表
 */
const MIME_TYPES = {
  // Office文档
  'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'xls': 'application/vnd.ms-excel',
  'doc': 'application/msword',
  'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'ppt': 'application/vnd.ms-powerpoint',
  'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // PDF
  'pdf': 'application/pdf',
  // 压缩文件
  'zip': 'application/zip',
  'rar': 'application/x-rar-compressed',
  '7z': 'application/x-7z-compressed',
  // 文本文件
  'txt': 'text/plain',
  'csv': 'text/csv',
  'json': 'application/json',
  'xml': 'application/xml',
  'html': 'text/html',
  'css': 'text/css',
  'js': 'application/javascript',
  // 图片
  'png': 'image/png',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'gif': 'image/gif',
  'bmp': 'image/bmp',
  'svg': 'image/svg+xml',
  'webp': 'image/webp',
  // 音视频
  'mp3': 'audio/mpeg',
  'mp4': 'video/mp4',
  'avi': 'video/x-msvideo',
  'wav': 'audio/wav'
}

/**
 * 从响应头中提取文件名
 * @param {Object} headers 响应头对象
 * @returns {String|null} 文件名或null
 */
function extractFileNameFromHeaders (headers) {
  if (!headers || !headers['content-disposition']) {
    return null
  }

  try {
    const disposition = decodeURI(headers['content-disposition'])

    // 尝试多种匹配模式
    const patterns = [
      /filename\*=UTF-8''(.+)/i,
      /filename="?(.+?)"?$/i,
      /fileName=(.+)$/i
    ]

    for (const pattern of patterns) {
      const match = disposition.match(pattern)
      if (match && match[1]) {
        return match[1].replace(/['"]/g, '').trim()
      }
    }
  } catch (error) {
    console.warn('解析文件名失败:', error)
  }

  return null
}

/**
 * 获取MIME类型
 * @param {String} extension 文件扩展名
 * @returns {String} MIME类型
 */
function getMimeType (extension) {
  return MIME_TYPES[extension.toLowerCase()] || 'application/octet-stream'
}

/**
 * 触发浏览器下载
 * @param {Blob} blob Blob对象
 * @param {String} fileName 文件名
 */
function triggerDownload (blob, fileName) {
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // IE10+ 浏览器
    window.navigator.msSaveOrOpenBlob(blob, fileName)
  } else {
    // 现代浏览器
    const link = document.createElement('a')
    const url = window.URL.createObjectURL(blob)

    link.href = url
    link.download = fileName
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()

    // 清理
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }, 100)
  }
}

/**
 * 从URL下载文件
 * @param {String} url 文件URL
 * @param {String} fileName 文件名（可选）
 */
export function downloadByUrl (url, fileName) {
  const link = document.createElement('a')
  link.href = url
  if (fileName) {
    link.download = fileName
  }
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    document.body.removeChild(link)
  }, 100)
}

/**
 * 从Base64下载文件
 * @param {String} base64 Base64字符串
 * @param {String} fileName 文件名
 * @param {String} mimeType MIME类型（可选）
 */
export function downloadByBase64 (base64, fileName, mimeType) {
  // 移除Base64前缀（如果存在）
  const base64Data = base64.replace(/^data:[^;]+;base64,/, '')

  // 将Base64转换为二进制
  const binaryString = window.atob(base64Data)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // 自动检测MIME类型
  if (!mimeType) {
    const extension = fileName.split('.').pop()
    mimeType = getMimeType(extension)
  }

  const blob = new Blob([bytes], { type: mimeType })
  triggerDownload(blob, fileName)
}

/**
 * 从文件流下载（通用方法）
 * @param {Blob|ArrayBuffer|String} data 文件数据
 * @param {String} fileName 文件名（不含扩展名）
 * @param {String} extension 文件扩展名（如 'xlsx', 'pdf' 等）
 */
export function downloadByData (data, fileName = '下载文件', extension = 'xlsx') {
  try {
    const mimeType = getMimeType(extension)
    const fullFileName = fileName.includes('.') ? fileName : `${fileName}.${extension}`

    let blob
    if (data instanceof Blob) {
      blob = data
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: mimeType })
    } else if (typeof data === 'string') {
      blob = new Blob([data], { type: mimeType })
    } else {
      throw new Error('不支持的数据类型')
    }

    triggerDownload(blob, fullFileName)
  } catch (error) {
    console.error('文件下载失败:', error)
    throw error
  }
}

/**
 * 从axios响应下载文件（自动解析文件名）
 * @param {Object} response axios响应对象
 * @param {String} defaultFileName 默认文件名（当无法从响应头解析时使用）
 */
export function downloadByResponse (response, defaultFileName = '下载文件') {
  try {
    if (!response || !response.data) {
      throw new Error('响应数据为空')
    }

    // 尝试从响应头获取文件名
    let fileName = extractFileNameFromHeaders(response.headers)

    // 如果没有获取到文件名，使用默认文件名
    if (!fileName) {
      fileName = defaultFileName
      // 如果默认文件名没有扩展名，尝试从Content-Type获取
      if (!fileName.includes('.')) {
        const contentType = response.headers['content-type']
        if (contentType) {
          const ext = Object.keys(MIME_TYPES).find(key => MIME_TYPES[key] === contentType)
          if (ext) {
            fileName = `${fileName}.${ext}`
          }
        }
      }
    }

    const blob = new Blob([response.data])
    triggerDownload(blob, fileName)
  } catch (error) {
    console.error('文件下载失败:', error)
    throw error
  }
}

/**
 * 下载JSON数据为文件
 * @param {Object|Array} data JSON数据
 * @param {String} fileName 文件名（不含扩展名）
 */
export function downloadJson (data, fileName = 'data') {
  const jsonString = JSON.stringify(data, null, 2)
  downloadByData(jsonString, fileName, 'json')
}

/**
 * 下载文本内容为文件
 * @param {String} text 文本内容
 * @param {String} fileName 文件名（不含扩展名）
 */
export function downloadText (text, fileName = 'file') {
  downloadByData(text, fileName, 'txt')
}

/**
 * 下载CSV数据
 * @param {Array<Array>} data 二维数组数据
 * @param {String} fileName 文件名（不含扩展名）
 */
export function downloadCsv (data, fileName = 'data') {
  const csvContent = data.map(row => row.join(',')).join('\n')
  const bom = '\uFEFF' // 添加BOM以支持中文
  downloadByData(bom + csvContent, fileName, 'csv')
}

// 默认导出（兼容旧代码）
export default {
  downloadByUrl,
  downloadByBase64,
  downloadByData,
  downloadByResponse,
  downloadJson,
  downloadText,
  downloadCsv
}
